import { useEffect } from 'react';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { useEntities } from '@/api/entities';
import { useEntityStore } from '@/store/entity';

/**
 * Topbar entity selector — lets the user pick which entity context they're
 * working in. The selection is persisted to localStorage so it survives
 * navigation and reloads. Pages read the active entity_id via useEntityStore.
 *
 * Auto-pick rule: when the API returns exactly ONE entity (the usual
 * case for ESS users — backend scopes /api/entities to the caller's own
 * entity for non-staff), set it as the active entity so downstream
 * scoped queries don't sit at "All entities" and pull nothing. For
 * staff with multiple entities we still leave the picker on
 * "All entities" by default so the previous-auto-select-the-wrong-one
 * bug doesn't come back.
 */
export function EntitySelector() {
  const { data: entities, isLoading } = useEntities();
  const entityId   = useEntityStore((s) => s.entityId);
  const setEntity  = useEntityStore((s) => s.setEntity);

  const active = entities?.filter((e) => !e.is_deleted) ?? [];

  // Defensive sync: if the persisted entityId no longer corresponds to
  // any active entity (e.g. entity was soft-deleted, DB was cleaned,
  // user is on a different tenant), drop it so the auto-pick below
  // can re-resolve and the topbar isn't stuck showing a phantom value.
  useEffect(() => {
    if (isLoading) return;
    if (!entityId) return;
    if (active.length > 0 && !active.some((e) => e.id === entityId)) {
      setEntity(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, active.length, entityId]);

  // Auto-select when there's exactly one entity available AND nothing
  // is currently selected. Runs once per data-load via useEffect to
  // avoid an infinite setState loop on render.
  useEffect(() => {
    if (!isLoading && active.length === 1 && !entityId) {
      setEntity(active[0].id);
    }
    // Intentionally only dependent on the count + current selection;
    // re-running on every render of `active` (a new array each render)
    // would still be safe because setEntity is idempotent at the store,
    // but the linter likes a stable list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, active.length, entityId]);

  // For a single-entity tenant, show a read-only chip with the entity
  // name instead of an interactive picker. Earlier versions hid the
  // selector entirely, which left users wondering which entity context
  // their pages were operating in (especially after the topbar
  // "Entity" label-only state from prior renders disappeared).
  if (!isLoading && active.length === 1) {
    const only = active[0];
    return (
      <Tooltip title="Only one active entity is configured for this tenant.">
        <Chip
          size="medium"
          variant="outlined"
          color="info"
          label={
            <span>
              <span style={{ fontSize: 10.5, color: '#8a8378', marginRight: 4 }}>Entity:</span>
              <span style={{ fontWeight: 700 }}>{only.short_code}</span>
              <span style={{ color: '#4a4742', marginLeft: 4 }}>· {only.name}</span>
              <span style={{ fontSize: 10.5, color: '#8a8378', marginLeft: 4 }}>({only.country_code})</span>
            </span>
          }
          sx={{ height: 36, fontSize: 13 }}
        />
      </Tooltip>
    );
  }

  // When nothing is selected yet (multi-entity staff), draw attention to
  // the picker so first-time users know where to start. The colored
  // border + label nudges them to the dropdown they need to open.
  const needsPick = !isLoading && !entityId && active.length > 1;

  return (
    <FormControl
      size="small"
      sx={{
        minWidth: 240,
        // Gentle brand-blue prompt to pick an entity — a soft tint + a thin
        // accent border, not a loud amber alarm.
        ...(needsPick && {
          '& .MuiOutlinedInput-root': { backgroundColor: '#f2f8fd' },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
            borderWidth: 1.5,
          },
          '& .MuiInputLabel-root': { color: 'primary.main', fontWeight: 600 },
        }),
      }}
    >
      <InputLabel>{needsPick ? 'Pick entity' : 'Entity'}</InputLabel>
      <Select
        label={needsPick ? 'Pick entity' : 'Entity'}
        // Only use the stored entityId once it's actually in the loaded option
        // set — otherwise MUI logs an "out-of-range value" warning on the first
        // render (entityId comes from localStorage before /entities resolves).
        value={entityId && active.some((e) => e.id === entityId) ? entityId : ''}
        onChange={(e) => setEntity(e.target.value || null)}
        disabled={isLoading}
      >
        <MenuItem value="">
          All entities
        </MenuItem>
        {active.map((e) => (
          <MenuItem key={e.id} value={e.id}>
            <span style={{ fontWeight: 600 }}>{e.short_code}</span>
            &nbsp;·&nbsp;
            <span style={{ color: '#4a4742' }}>{e.name}</span>
            &nbsp;
            <span style={{ fontSize: 11, color: '#8a8378' }}>({e.country_code})</span>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
